<template>
    <Modal
      :active.sync="isOpen"
      title="Cadastro de Workflow"
      class="workflow-modal"
      id="popup-n8n-workflow"
      @close="closeModal"
    >
      <vs-form @submit.prevent="handleSubmit">
        <div class="form-container">
          <div class="form-group">
            <label>Nome do fluxo</label>
            <vs-input
              v-model="formData.flowName"
              placeholder="Digite o nome do workflow"
              class="form-vs-input"
              :class="{ 'error-border': errors.flowName }"
              @input="errors.flowName = false"
            />
            <span v-if="errors.flowName" class="error-text">Este campo é obrigatório</span>
          </div>

          <div class="form-group">
            <label>Path</label>
            <vs-input
              v-model="formData.path"
              placeholder="Digite o path"
              class="form-vs-input"
              :class="{ 'error-border': errors.path }"
              @input="errors.path = false"
            />
            <span v-if="errors.path" class="error-text">Este campo é obrigatório</span>
          </div>

          <div class="form-group">
            <label>Executar em</label>
            <v-select
              v-model="formData.executionStageCode"
              :options="executionStages"
              :reduce="stage => stage.value"
              placeholder="Selecione um estágio"
              label="label"
              class="custom-select"
              :class="{ 'error-border': errors.executionStageCode }"
            />
            <span v-if="errors.executionStageCode" class="error-text">Selecione um estágio</span>
          </div>

          <div class="form-group">
            <label>Campos que disparam validação</label>
            <v-select
              v-model="formData.triggerFields"
              :options="triggerFieldsOptions"
              label="label"
              placeholder="Selecione os campos"
              multiple
              searchable
              :close-on-select="false"
              :reduce="(item) => item.value"
              class="custom-select"
            >
              <template #selected-option-container="{ option }">
                <vs-chip
                  class="mt-2 ml-1"
                  transparent
                  color="primary"
                  closable
                  @click="removeTriggerField(option.value)"
                >
                  {{ option.label }}
                </vs-chip>
              </template>

              <template #option="{ label, value }">
                <div :class="{ 'vs-select--is-selected': formData.triggerFields.includes(value) }">
                  <feather-icon
                    v-if="formData.triggerFields.includes(value)"
                    icon="CheckCircleIcon"
                    svgClasses="h-4 w-4"
                  />
                  {{ label }}
                </div>
              </template>
            </v-select>
          </div>

          <div class="switch-group">
            <label>Ativo?</label>
            <vs-switch v-model="formData.isActive" />
          </div>

          <div class="form-actions">
            <vs-button @click="handleSubmit" color="success" block :disabled="isSubmitting">
              Salvar
            </vs-button>
            <vs-button @click="closeModal" color="danger" outline block>
              Cancelar
            </vs-button>
          </div>
        </div>
      </vs-form>
    </Modal>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import n8n from "../../../../../services/n8n";
import { EXECUTION_STAGES } from "../../../../../constants/executionStages";
import Modal from "../../../../../components/Modal.vue";
import {TRIGGER_VALIDATION_FIELDS_OPTIONS} from "../../../../../constants/triggerValidationFields";

const defaultFormData = {
  id: null,
  flowName: "",
  path: "",
  executionStageCode: "",
  isActive: false,
  triggerFields: [],
};

export default {
  name: "WorkflowModal",
  components: { Modal, vSelect },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    workflow: {
      type: Object,
      default: () => null,
    },
    executionStages: {
      type: Array,
      default: () => null,
    },
  },
  data() {
    return {
      formData: { ...defaultFormData },
      executionStages: EXECUTION_STAGES,
      triggerFieldsOptions: TRIGGER_VALIDATION_FIELDS_OPTIONS,
      errors: {},
      isSubmitting: false,
    };
  },
  watch: {
    workflow: {
      immediate: true,
      handler(newWorkflow) {
        if (newWorkflow) {
          const normalizedTriggerFields = Array.isArray(newWorkflow.triggerFields)
            ? newWorkflow.triggerFields.map(field =>
              typeof field === 'object' ? field.value : field
            )
            : [];

          this.formData = {
            ...defaultFormData,
            ...newWorkflow,
            triggerFields: normalizedTriggerFields,
          };
        } else {
          this.resetForm();
        }
      },
    },
    formData: {
      deep: true,
      handler(newFormData) {
        if (newFormData.executionStageCode) {
          this.errors.executionStageCode = false;
        }
      },
    },
  },
  methods: {
    validateForm() {
      this.errors = {};

      if (!this.formData.flowName) this.errors.flowName = true;
      if (!this.formData.path) this.errors.path = true;
      if (!this.formData.executionStageCode) this.errors.executionStageCode = true;

      return Object.keys(this.errors).length === 0;
    },

    removeTriggerField(value) {
      this.formData.triggerFields = this.formData.triggerFields.filter((f) => f !== value);
    },

    async handleSubmit(event) {
      if (event && event.preventDefault) {
        event.preventDefault();
      }

      if (!this.validateForm()) {
        this.$vs.notify({
          position: "top-center",
          title: "Erro",
          text: "Preencha todos os campos obrigatórios antes de salvar.",
          color: "warning",
        });
        return;
      }

      if (this.isSubmitting) return;
      this.isSubmitting = true;
      this.$vs.loading();

      try {
        const { isTrusted, ...payload } = {
          ...this.formData,
          triggerFields: this.formData.triggerFields.map(field =>
            typeof field === 'object' ? field.value : field
          ),
          isActive: this.formData.isActive || false,
        };

        let savedWorkflow;
        if (this.formData.id) {
          savedWorkflow = await n8n.updateWorkflow(payload);
        } else {
          savedWorkflow = await n8n.createWorkflow(payload);
        }

        this.$emit("save");
        this.$vs.notify({
          position: "top-center",
          title: "Sucesso",
          text: `Workflow ${savedWorkflow.flowName} salvo com sucesso`,
          color: "success",
        });

        this.closeModal();
      } catch (error) {
        this.$vs.notify({
          position: "top-center",
          title: "Atenção",
          text: `Erro ao salvar o workflow, verifique se não há outro workflow ativo para o mesmo estágio`,
          color: "danger",
        });
      } finally {
        this.isSubmitting = false;
        this.$vs.loading.close();
      }
    },

    closeModal() {
      this.resetForm();
      this.errors = {};
      this.$emit("close");
    },
    resetForm() {
      this.formData = { ...defaultFormData };
      this.errors = {};
    },
  },
};
</script>

<style>
#popup-n8n-workflow {
  width: 100%;
}

#popup-n8n-workflow .form-container {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

#popup-n8n-workflow .form-group {
  display: flex;
  flex-direction: column;
  position: relative;
}

#popup-n8n-workflow .form-vs-input {
  width: 100%;
}

#popup-n8n-workflow label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #444;
}

#popup-n8n-workflow .custom-select {
  width: 100%;
}

#popup-n8n-workflow .switch-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

#popup-n8n-workflow .form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

#popup-n8n-workflow .error-border {
  border: 1px solid red !important;
}

#popup-n8n-workflow .error-text {
  color: red;
  font-size: 12px;
  margin-top: 4px;
}

#popup-n8n-workflow .vs-popup {
  min-width: 960px;
}

#popup-n8n-workflow .vs-select--is-selected {
  color: #00bfed;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
